import { formatMoney } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { orgHref } from '../lib/public-router'
import { readCivilDate, useToday } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { Button, Card, Figure, Notice } from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import './gift.css'

type GiftView = {
  toward: string | null
  rateCents: number
  cadence: 'monthly' | 'annual'
  startsOn: string
}

/**
 * The figure a gift's link opens on: what it gives, how often, toward what,
 * and since — or, not yet begun, from — when.
 */
export function GiftFigure({ view }: { view: GiftView }) {
  const today = useToday()
  const each = view.cadence === 'annual' ? 'a year' : 'a month'
  const when = `${view.startsOn > today ? 'Starting' : 'Since'} ${readCivilDate(view.startsOn, 'long')}.`
  return (
    <Figure
      amount={`${formatMoney(view.rateCents)} ${each}`}
      label={view.toward ? `Toward ${view.toward}. ${when}` : when}
    />
  )
}

/**
 * The page a monthly or yearly gift's link opens, for stopping it. No login:
 * the `?token=` is the whole authority, and it reaches only the address the
 * gift was set up from. It is read on the page of the org the gift belongs to.
 *
 * The page is the decision: it states the gift, says what stopping does, and
 * the one button stops it at once (product owner, 2026-10-11). A gift already
 * stopped says so, and a link that does not verify says that rather than
 * erroring.
 */
export function StopGiftPage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const link = { shortId: orgId, token }

  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const gift = useQuery({
    ...trpc.donation.giftStopView.queryOptions(link),
    enabled: token.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const stop = useMutation({
    ...trpc.donation.stopGift.mutationOptions(),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: trpc.donation.giftStopView.queryKey(link),
      }),
  })

  /* The button goes once it has done its work, so the sentence saying what
     happened takes the keyboard in its place. */
  const done = useRef<HTMLDivElement>(null)
  const stopped = gift.data?.stopped ?? false
  useEffect(() => {
    if (stop.isSuccess && stopped) done.current?.focus()
  }, [stop.isSuccess, stopped])

  if (org.isLoading || org.error || !org.data) {
    return (
      <PublicShell name={null} orgId={null} onLogin={onLogin}>
        {org.isLoading ? (
          <div className="gift">
            <Card className="gift__card">
              <Notice>Loading…</Notice>
            </Card>
          </div>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }

  const o = org.data
  const view = gift.data ?? null

  return (
    <PublicShell
      name={o.name}
      nameHe={o.nameHe}
      language={o.publicLanguage}
      orgId={o.shortId}
      acceptsDonations={o.acceptsDonations}
      contact={o}
      onLogin={onLogin}
    >
      <div className="gift">
        <Card className="gift__card">
          <h1 className="gift__title">Your recurring gift</h1>
          {token && gift.isLoading ? (
            <p className="gift__muted">Loading…</p>
          ) : !token || gift.isError || !view ? (
            <Notice>
              {gift.error?.message ??
                'This link is not valid. Please use the link in your receipt, or contact the office.'}
            </Notice>
          ) : (
            <>
              <GiftFigure view={view} />
              {view.stopped ? (
                <div ref={done} tabIndex={-1}>
                  <Notice tone="info">
                    This gift is stopped. Your card will not be charged for it
                    again.
                  </Notice>
                </div>
              ) : view.awaitingConfirmation ? (
                <Notice>
                  This gift is waiting for you to confirm it from the letter we
                  sent. Until you do, nothing is set up and nothing is charged.
                </Notice>
              ) : (
                <>
                  <p className="gift__muted">
                    Stopping it takes effect now: your card is not charged for
                    it again. Anything it already owes stays on your account.
                  </p>
                  {stop.error && (
                    <Notice tone="error">{stop.error.message}</Notice>
                  )}
                  <Button
                    variant="danger"
                    disabled={stop.isPending}
                    onClick={() => stop.mutate(link)}
                  >
                    Stop this gift
                  </Button>
                  {/* A gift on a card of its own may want a new card rather
                      than an end, so the way to one sits beside Stop. */}
                  {view.changeCardToken && (
                    <Button
                      variant="secondary"
                      render={
                        <a
                          href={`${orgHref(o.shortId, 'change-card')}?token=${encodeURIComponent(view.changeCardToken)}`}
                        />
                      }
                    >
                      Use a different card
                    </Button>
                  )}
                </>
              )}
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </>
          )}
        </Card>
      </div>
    </PublicShell>
  )
}
